import pluralize from "pluralize";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { titleCase } from "title-case";
import { Item, MenuOptionItem, MenuTriggerButton, Section, VStack } from "widgets";

import {
  createSequenceAnalyzerSession,
  sequenceViewerSupportedTypes,
  validateSequences,
  WEB_ANALYZER_KEYS,
} from "@/api/sequenceAnalyzer.ts";
import { canHaveGroupInClassicUiOption } from "@/app-utils/entity.ts";
import { routeWithBasename } from "@/app-utils/route.ts";
import { type Configuration, type Organism } from "@/graphql.ts";
import { isClassicUiDisabled } from "@/redux/modules/config.ts";
import {
  createGroupInClassicPortal,
  forwardToAnalyzer,
  getTableQuerySpecWithSelection,
  hasSelection,
  type ITableData,
} from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { showConfirmationDialog } from "@/services/ConfirmationService.ts";
import { ANALYZERS, CLASSIC_UI, ENTITY_TYPE, FEATURE_DISPLAY_NAME, VIEW, WEB_ANALYZERS } from "@/settings/settings.ts";

interface ClassicPortalProps {
  viewType: string;
  table: ITableData;
  entity?: { id: number; groupType: string }; // for summary pages
  innerEntityType: string; // for summary pages
  // need a name for mapping group of entities to the classic portal
  getParentEntityName?: () => string;
}

export function ToolsButton(props: ClassicPortalProps) {
  const dispatch = useDispatch<ReduxDispatch>();
  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);
  const { table, innerEntityType, entity } = props;
  const typeLabel = FEATURE_DISPLAY_NAME[innerEntityType];
  const selectedIds: number[] = useMemo(() => {
    if (table) {
      return table.selectionModel.selectedIds.length > 0
        ? (table.selectionModel.selectedIds as number[])
        : table?.rowIds;
    }
    return entity?.id != null ? [entity.id] : [];
  }, [entity?.id, table]);

  const webBasedAnalyzers = useMemo(() => {
    const allSupportedTypes = [...sequenceViewerSupportedTypes.gene, ...sequenceViewerSupportedTypes.protein];

    return allSupportedTypes.includes(innerEntityType) ? [WEB_ANALYZER_KEYS.SEQUENCE_ANALYZER] : null;
  }, [innerEntityType]);

  // const entityPhrase = typeLabel && pluralizeWord(typeLabel, table ? 2 : 1);
  const analyzers = useMemo(() => {
    let analyzers: string[] = [];

    switch (innerEntityType) {
      case ENTITY_TYPE.GENE:
      case ENTITY_TYPE.TRANSCRIPT:
      case ENTITY_TYPE.TRANSCRIPT_LOCATION:
        analyzers = [ANALYZERS.LOCUS_ANALYZER, ANALYZERS.DNA_ANALYZER, ANALYZERS.PATHWAY_ANALYZER];
        break;
      case ENTITY_TYPE.OTHER:
      case ENTITY_TYPE.PROBE:
        analyzers = [ANALYZERS.DNA_ANALYZER];
        break;
      case ENTITY_TYPE.PROTEIN:
      case ENTITY_TYPE.PROTEIN_LOCATION:
        analyzers = [ANALYZERS.LOCUS_ANALYZER, ANALYZERS.PROTEIN_ANALYZER, ANALYZERS.PATHWAY_ANALYZER];
        break;
      case ENTITY_TYPE.ORGANISM:
      case ENTITY_TYPE.CONTIG:
        analyzers = [ANALYZERS.LOCUS_ANALYZER];
        break;
    }

    if (analyzers.length < 1 || isClassicUiDisabled(configuration)) return null;
    return analyzers;
  }, [innerEntityType]);

  const buttonDisabledTooltip = getDisabledTooltipText();

  const openSequenceViewer = useCallback(() => {
    createSequenceAnalyzerSession(selectedIds, innerEntityType).then((sessionId) =>
      window.open(routeWithBasename(`/visualizer/sequence?sessionId=${sessionId}`), "_blank"),
    );
  }, [innerEntityType, selectedIds]);

  if (
    !((!!table && canHaveGroupInClassicUiOption(innerEntityType, configuration)) || !!analyzers || !!webBasedAnalyzers)
  )
    return null;

  function copyToClassicPortal(labelType: string, table: ITableData) {
    // Create a name based on:
    //    a saved group
    //    a temporary group
    //    a sub table for an entity in entity overview mode form a group or a separate entity page
    //    a feature table for a genome in GV in summary mode of an entity (is standalone GV different? untested!)
    //    a contig list from ContentBrowser
    //
    const hasSelectedItems = hasSelection(table);
    const titleType = labelType && pluralize.plural(labelType);

    const groupName = props.getParentEntityName
      ? (hasSelectedItems ? `Selected ${titleType} from ` : `${titleCase(titleType)} from `) +
        props.getParentEntityName()
      : titleType;

    dispatch(
      createGroupInClassicPortal({
        groupName,
        tableQuerySpec: getTableQuerySpecWithSelection(table),
      }),
    );
  }

  /**
   * Disables the whole Tools button in case the entity is not supported by any Analyzer
   */
  function getDisabledTooltipText() {
    // Disable the button if there are no rows in a table
    if (table && table.rowIds?.length === 0) {
      return "At least one row must be selected to send to the Analyzers";
    } else if (
      props.viewType === VIEW.ENTITY_SUMMARY_VIEW.type &&
      innerEntityType === ENTITY_TYPE.ORGANISM &&
      (entity as Organism)?.sequenced === false
    ) {
      // SEL6-3591
      return "Genome is not sequenced and cannot be sent to the Analyzers";
    } else if (analyzers && analyzers.length === 0 && webBasedAnalyzers && webBasedAnalyzers?.length === 0) {
      return "No matching Analyzers";
    }
    return undefined;
  }

  return (
    <MenuTriggerButton
      isDisabled={buttonDisabledTooltip}
      icon={"arrow_outward"}
      tooltip={`Open selected ${pluralize.plural(typeLabel).toLowerCase()} in one of the Analyzers`}
      label={"Tools"}
      showLabel
      data-e2e-label={"analysis-tools"}
      onAction={(key) => {
        switch (key) {
          case "create-classic-ui-group":
            copyToClassicPortal(typeLabel, table);
            break;
          case WEB_ANALYZER_KEYS.SEQUENCE_ANALYZER:
            validateSequences(selectedIds, innerEntityType).then((validationResult) => {
              if (validationResult.length > 0) {
                showConfirmationDialog({
                  title: "Missing Sequences Detected",
                  message: (
                    <VStack>
                      The following identifiers have no sequences attached and cannot be displayed:
                      <ul>
                        {validationResult.slice(0, 5).map((res, index) => (
                          <li key={`validation-result-${index}`}>{res}</li>
                        ))}
                        {validationResult.length > 3 ? <li key={"more-indicator"}>...</li> : null}
                      </ul>
                    </VStack>
                  ),
                  mainButton: {
                    label: "Continue",
                    onPress: openSequenceViewer,
                  },
                  dataE2eLabel: "missing-sequences-warning",
                });
              } else {
                openSequenceViewer();
              }
            });
            break;
          default:
            // The key is the analyzer name
            dispatch(forwardToAnalyzer(table, entity?.id, innerEntityType, String(key)));
            break;
        }
      }}
    >
      {webBasedAnalyzers != undefined ? (
        <Section title={"Web UI"} key={`web-analyzer-section`}>
          {webBasedAnalyzers.map((analyzer) => (
            <Item key={analyzer}>
              <MenuOptionItem id={analyzer} icon={"arrow_outward"} title={WEB_ANALYZERS.SEQUENCE_ANALYZER} />
            </Item>
          ))}
        </Section>
      ) : null}

      {/* Only show this section when there is something to show */}
      {(!!table && canHaveGroupInClassicUiOption(innerEntityType, configuration)) || analyzers ? (
        <Section title={CLASSIC_UI}>
          {!!table && canHaveGroupInClassicUiOption(innerEntityType, configuration) ? (
            <Item key={"create-classic-ui-group"}>
              <MenuOptionItem id={"create-classic-ui-group"} icon={"table"} title={"Table Display"} />
            </Item>
          ) : null}

          {analyzers ? (
            <>
              {analyzers.map((analyzer) => (
                <Item key={analyzer}>
                  <MenuOptionItem
                    id={`send-to-analyzer-menu-item-${analyzer}`}
                    icon={"arrow_outward"}
                    title={analyzer}
                  />
                </Item>
              ))}
            </>
          ) : null}
        </Section>
      ) : null}
    </MenuTriggerButton>
  );
}
